import React, { useRef, useState, useEffect, useLayoutEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import AggregateDetailsContainer from '../containers/details_container/aggregate_details_container';
import InfoTooltip from './info_tooltip';
import TableCell from './table_cell';
import { resolveCutLineLabel } from '../utils/cut_line_label_utils';
import { themedTextClass } from '../utils/css_utils';
import { VIEW_MODES, STAR_PULSE_MS } from '../constants';
import { hostWindow } from '../../shared/host_window'
import { splitThru } from '../utils/thru_utils'
import { detailsKeyFor } from '../utils/details_utils'
import { stateFlagUrlFor } from '../utils/flag_utils'
import { componentText } from '../utils/i18n_utils'
import useNoHorizontalChaining from '../hooks/useNoHorizontalChaining'

const AggregateRow = ({
  columns = [],
  aggId,
  agg,
  toggleFavorite,
  isFavorite = false,
  fetchDetails,
  removeDetails,
  eventId,
  details = {},
  rounds,
  viewMode,
  displayCutLine,
  cutListLabel,
  resolveTiesHref,
  sectionLine,
  colorScores,
  displayedFlags,
  stateFlagUrls,
  isManagerSite,
}) => {
  if (!agg) return null;

  const columnIds = new Set((columns || []).map((column) => column.id));
  const memberId = agg.memberIds?.[0];
  if (!memberId) return null;

  // The open scorecard follows the players, not the aggregate id: a rescore
  // renews every aggregate id, and this row then remounts under the new one.
  const detailsKey = detailsKeyFor(agg.memberIds);
  const isOpen = !!details[detailsKey];

  // Starring inserts the player into the favourites list above, which pushes
  // this row down and leaves the cursor over a different player. Hold the
  // clicked row where it was so a second click lands where it was aimed.
  const rowRef = useRef(null)
  const anchorTopRef = useRef(null)
  const detailsRowRef = useRef(null)

  useNoHorizontalChaining(detailsRowRef, '.lbv2__details-scroll .lbv2__scroll-wrapper', isOpen)

  // A class, not a :hover-style rule: the star keeps its element across the
  // toggle, so an animation tied to the filled/outline class would also run
  // for every already-favourited row on mount.
  const [ starPulsing, setStarPulsing ] = useState(false)

  const handleFavoriteClick = useCallback(() => {
    anchorTopRef.current = rowRef.current?.getBoundingClientRect().top ?? null
    setStarPulsing(true)
    toggleFavorite(eventId, memberId)
  }, [ toggleFavorite, eventId, memberId ])

  useEffect(() => {
    if (!starPulsing) {
      return undefined
    }

    const timer = setTimeout(() => setStarPulsing(false), STAR_PULSE_MS)
    return () => clearTimeout(timer)
  }, [ starPulsing ])

  // Before paint, so the row never renders at the shifted position first.
  useLayoutEffect(() => {
    const before = anchorTopRef.current
    anchorTopRef.current = null
    if (before === null) {
      return
    }

    const after = rowRef.current?.getBoundingClientRect().top
    if (after === undefined) {
      return
    }

    const delta = after - before
    if (Math.abs(delta) < 1) {
      return
    }

    // The host page owns the scroll when the widget is embedded.
    hostWindow().scrollBy(0, delta)
  }, [ isFavorite ])

  const handlePlayerClick = () => {
    isOpen ? removeDetails(eventId, detailsKey) : fetchDetails(eventId, aggId, detailsKey);
  };

  const getScoreColor = (score = '') => {
    if (score.length > 1 && score.startsWith('+')) return colorScores.worse;
    if (score.length > 1 && score.startsWith('-')) return colorScores.better;
    if (score === 'E') return colorScores.equal;
    return '#333F48';
  };

  const renderThru = (value) => {
    const { time, hole } = splitThru(value)

    if (!hole) {
      return <span className="lbv2__thru-time">{time}</span>
    }

    return (
      <>
        <span className="lbv2__thru-time">{time}<span className="lbv2__thru-sep">,</span></span>
        {' '}
        <span className="lbv2__thru-hole">{hole}</span>
      </>
    )
  }

  const pastRounds = agg?.pastRounds ?? [];
  const inProgressPastRounds = pastRounds.filter((pastRound) => rounds[pastRound.id].inProgress);
  const hasThruToParColumns = columnIds.has('thru') || inProgressPastRounds.length > 0;
  const totalBeforePastRounds = viewMode === VIEW_MODES.MOBILE && pastRounds.length > 0 && !hasThruToParColumns && columnIds.has('total');

  // An in-progress round carries its course abbreviation on its thru cell;
  // a completed round keeps it under its round total (same rule as the
  // single-round score/thru columns below).
  const renderPastRoundPairs = () => inProgressPastRounds.map((pastRound, idx) => (
    <React.Fragment key={pastRound?.id ?? idx}>
      <TableCell className="lbv2__past_round_thru">
        <div>{renderThru(pastRound.thru)}</div>
        {pastRound.courseAbbreviationsDisplay && (
          <div className="lbv2__course">{pastRound.courseAbbreviationsDisplay}</div>
        )}
      </TableCell>
      <TableCell className="lbv2__past_round_to_par">
        <div style={{ color: getScoreColor(pastRound?.score ?? '') }}>{pastRound.score}</div>
      </TableCell>
    </React.Fragment>
  ));

  const renderPastRoundTotals = () => pastRounds.map((pastRound, idx) => (
    <TableCell key={pastRound?.id ?? idx} className="lbv2__past_round_total">
      <div className="lbv2__cell-value" style={{ color: getScoreColor(pastRound?.score ?? '') }}>
        {pastRound.total}
        {/* Only the round that introduced the disposition: its child
            aggregate renders the disposition string itself as the round
            total ("DQ"), while other rounds keep their real scores. */}
        <InfoTooltip content={agg.disposition && pastRound.total === agg.disposition ? agg.dispositionCause : ''} />
      </div>
      {!rounds[pastRound.id]?.inProgress && pastRound.courseAbbreviationsDisplay && (
        <div className="lbv2__course">{pastRound.courseAbbreviationsDisplay}</div>
      )}
    </TableCell>
  ));

  // The info circle flows inline AFTER the value (icon to the right of the
  // text), so a narrow cell wraps it below naturally - which is why it sits
  // inside the value element: the cell content is a column flex, where a
  // sibling would always stack.
  const renderTotalCell = () => (
    <TableCell className="lbv2__total">
      <div className="lbv2__cell-value">
        {agg.total}
        <InfoTooltip content={agg.dispositionCause} />
      </div>
    </TableCell>
  );

  const renderArrow = (posChange) => {
    if (posChange === 0) return null;
    const isPositive = posChange > 0;
    return (
      <div className={`lbv2__move-arrow fas ${isPositive ? 'lbv2__arrow-up fa-arrow-up' : 'lbv2__arrow-down fa-arrow-down'}`} />
    );
  };

  return (
    <>
      {displayCutLine && (
        <tr className="lbv2__cutline-row">
          <td className="lbv2__cutline sticky" colSpan={100}>
            <div className="lbv2__cutline-text">
              {resolveCutLineLabel(cutListLabel) || componentText('aggregate_row.players_not_in_cut', 'The following players did not make the cut.')}
              {resolveTiesHref && (
                <a className="lbv2__resolve-ties-btn" href={resolveTiesHref}>
                  <span className="lbv2__resolve-ties-icon fas fa-arrow-up-arrow-down" />
                  {componentText('aggregate_row.resolve_ties', 'Resolve Ties')}
                </a>
              )}
            </div>
          </td>
        </tr>
      )}
      {sectionLine && (
        <tr className="lbv2__section-line-row">
          <td className="lbv2__section-line sticky" colSpan={100}>
            <div className="lbv2__section-line-text">{sectionLine}</div>
          </td>
        </tr>
      )}
      <tr className="lbv2__aggregate-row" id={aggId} ref={rowRef}>
        {columns.map((column) => {
          const entryKey = column.id;
          if (entryKey === 'affiliation') return null;

          switch (entryKey) {
            case 'position':
              return (
                <React.Fragment key={entryKey}>
                  <TableCell className="lbv2__position sticky">
                    <div className="lbv2__cell-value">
                      {agg.position}
                      <InfoTooltip content={agg.dispositionCause} />
                    </div>
                  </TableCell>

                  {!isManagerSite && (
                    <TableCell className="lbv2__favorite sticky">
                      <button
                        className={`lbv2__favorite-star fa-star fa${isFavorite ? 's' : 'r'} ${starPulsing ? 'is-pulsing' : ''} ${themedTextClass(isManagerSite)}`}
                        onClick={handleFavoriteClick}
                      />
                    </TableCell>
                  )}

                  {viewMode !== VIEW_MODES.MOBILE && agg.posChange !== undefined && (
                    <TableCell className="lbv2__move sticky">
                      {Math.abs(agg.posChange) !== 0
                        ? (
                          <>
                            {renderArrow(agg.posChange)}
                            <div className="lbv2__move-number">{Math.abs(agg.posChange)}</div>
                          </>
                        ) : (
                          <>
                            <span className="lbv2__vector19" />
                            <div className="lbv2__move-number">-</div>
                          </>
                        )}
                    </TableCell>
                  )}
                </React.Fragment>
              );

            case 'name':
              return (
                <React.Fragment key={entryKey}>
                  {displayedFlags === 'country_flags' && (
                    <TableCell className="lbv2__flags sticky">
                      {agg.country.map((code, i) => (
                        <span key={i} className={`flag-icon country-flag flag-icon-${code.alpha_2?.toLowerCase()}`} />
                      ))}
                    </TableCell>
                  )}

                  {displayedFlags === 'state_flags' && (
                    <TableCell className="lbv2__flags sticky">
                      {(agg.stateIsos ?? []).map((code, i) => {
                        const url = stateFlagUrlFor(code, stateFlagUrls);
                        return url && (
                          <span key={`${i}-${code}`} className="flag-icon state-flag" style={{ backgroundImage: `url(${url})` }} />
                        );
                      })}
                    </TableCell>
                  )}

                  <TableCell className="lbv2__name sticky">
                    <div className="lbv2__player">
                      <button className="lbv2__player-btn" onClick={handlePlayerClick}>
                        {viewMode === VIEW_MODES.MOBILE
                          ? `${agg.memberNames[0].firstName[0]}. ${agg.memberNames[0].lastName}`
                          : agg.name
                        }
                      </button>
                      {viewMode !== VIEW_MODES.MOBILE && agg.customLogos?.length > 0 && (
                        <div className="lbv2__display-custom-trophy">
                          {agg.customLogos.map((url, i) => (
                            <img
                              // Keyed by URL so a refreshed presigned URL
                              // remounts the element — the onError hide below
                              // is imperative and would survive a src swap.
                              key={`${i}-${url}`}
                              className="lbv2__custom-leaderboard-logo"
                              src={url}
                              alt=""
                              draggable={false}
                              // Logo URLs are presigned and baked into the cached
                              // blob; an expired one must vanish, not render the
                              // broken-image glyph.
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                    {columnIds.has('affiliation') && (
                      // One box per word so a word wider than the mobile name
                      // cell ends in an ellipsis instead of being clipped;
                      // lines still break only between words (RTM-9795).
                      <div className="lbv2__affiliation">
                        {agg.affiliation.split(/\s+/).filter(Boolean).map((word, index) => (
                          <React.Fragment key={index}>
                            {index > 0 && ' '}
                            <span className="lbv2__affiliation-word">{word}</span>
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                  </TableCell>
                </React.Fragment>
              );

            case 'score':
              return (
                <React.Fragment key={entryKey}>
                  <TableCell className="lbv2__score">
                    <div style={{ color: getScoreColor(agg.score) }}>{agg[entryKey]}</div>
                    {/* While the round is in progress the thru column carries
                        the course abbreviation instead. */}
                    {!columnIds.has('thru') && agg.courseAbbreviationsDisplay && (
                      <div className="lbv2__course">{agg.courseAbbreviationsDisplay}</div>
                    )}
                  </TableCell>
                  {totalBeforePastRounds && renderTotalCell()}
                  {renderPastRoundPairs()}
                  {renderPastRoundTotals()}
                </React.Fragment>
              );

            case 'thru':
              return (
                <TableCell key={entryKey} className="lbv2__thru">
                  <div>{renderThru(agg[entryKey])}</div>
                  {agg.courseAbbreviationsDisplay && (
                    <div className="lbv2__course">{agg.courseAbbreviationsDisplay}</div>
                  )}
                </TableCell>
              );

            case 'total':
              if (totalBeforePastRounds) return null;
              return (
                <TableCell key={entryKey} className="lbv2__total">
                  <div className="lbv2__cell-value">
                    {agg[entryKey]}
                    <InfoTooltip content={agg.dispositionCause} />
                  </div>
                </TableCell>
              );

            default:
              return (
                <TableCell key={entryKey} className={`lbv2__${entryKey}`}>
                  <div>{agg[entryKey]}</div>
                </TableCell>
              );
          }
        })}
      </tr>

      {isOpen && (
        <tr className="lbv2__details-row">
          <td colSpan={100} ref={ detailsRowRef }>
            <AggregateDetailsContainer
              eventId={eventId}
              detailsKey={detailsKey}
              viewMode={viewMode}
            />
          </td>
        </tr>
      )}
    </>
  );
};

AggregateRow.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    titleKey: PropTypes.string,
    titleText: PropTypes.string,
  })),
  aggId: PropTypes.string.isRequired,
  agg: PropTypes.object,
  toggleFavorite: PropTypes.func.isRequired,
  isFavorite: PropTypes.bool,
  fetchDetails: PropTypes.func.isRequired,
  removeDetails: PropTypes.func.isRequired,
  eventId: PropTypes.string.isRequired,
  details: PropTypes.object,
  rounds: PropTypes.object,
  viewMode: PropTypes.oneOf(Object.values(VIEW_MODES)),
  displayCutLine: PropTypes.bool,
  cutListLabel: PropTypes.object,
  resolveTiesHref: PropTypes.string,
  sectionLine: PropTypes.string,
  colorScores: PropTypes.object,
  displayedFlags: PropTypes.string,
  stateFlagUrls: PropTypes.objectOf(PropTypes.string),
  isManagerSite: PropTypes.bool,
};

export default AggregateRow;
